import React, { useEffect, useMemo } from "react";
import _ from 'lodash';
import cx from 'classnames';

import styles from './card.module.scss';
import Thumbnail from "../../../../../components/thumbnail";
import useMeasure from "react-use-measure";
import { FileService } from "../../../../../utils/file.service";
import Reveal from "../../../../../components/animation";
import { CollectionAsset, CollectionEditState } from "../../../../../types/collections";
import Tooltip from "src/components/tooltip";
import { NavigateEntity } from "src/components/NavigateEntity";

interface AssetCardProps {
  asset: CollectionAsset;
  collection : CollectionEditState;
  sectionIndex? : number;
  editable: boolean;
  // eslint-disable-next-line no-unused-vars
  onDimensionsChange?: (dimensions: {width: number, height: number}) => void;
  // eslint-disable-next-line no-unused-vars
  onRemove?: (asset: CollectionAsset) => void;
  // eslint-disable-next-line no-unused-vars
  onClick: (asset: CollectionAsset) => void;
}

const AssetCard: React.FC<AssetCardProps> = ({ asset, editable, onDimensionsChange, onRemove, onClick, collection, sectionIndex }) => {
  const [ref, bounds] = useMeasure();

  const assetItem = asset.asset;
  const fileType = useMemo(() => {
    return FileService.getFileType(assetItem.metadata);
  }, [assetItem]);
  
  const fileSize = useMemo(() => {
    const size = _.get(assetItem, 'metadata.content_length');
    if(!size) return null;
    return FileService.formatSize(size);
  }, [assetItem]);

  const internal = !_.get(assetItem, 'settings.share', false);

  useEffect(() => {
    if(onDimensionsChange){
      onDimensionsChange(bounds);
    }
  }, [bounds]);

  const handleRemove = (e) => {
    e.preventDefault();
    if(onRemove){
      onRemove(asset);
    }
  }

  const handleClick = (e) => {
    e.preventDefault();
    if(!editable && onClick){
      onClick(asset);
    }
  }


  const getAssetUrl = (asset: CollectionAsset, section: number) => {
    const sectionIndex = section ? section : 0;
    let source = `collections/${collection.id}`;
    if (sectionIndex >= 0) {
      source = `${source}/sections/${sectionIndex}`;
    }
    return `/assets/${asset.asset.id}?source=${source}`;
  };

  const assetUrl = getAssetUrl(asset, sectionIndex ?? 0);

  return (
      <div ref={ref} className={cx(styles.card, {[styles.editable]: editable})} >
      <NavigateEntity
      path={assetUrl}
      onClick={handleClick}
      disabledClick={editable}
      >
      <div className={styles.icon}>
        <Thumbnail media={assetItem.icon} defaultImage="/images/assets/default_asset.jpg" />
        <div className={styles.fileType}>{fileType}</div>
        {internal && 
          <div className={styles.indicators}>
          <Tooltip content="Internal use only" placement="auto">
            <i className="paperflite-lock" />
          </Tooltip>
          </div>
        }
        <Reveal show={editable}>
          <div className={styles.overlay}></div>
          <button className={styles.remove} onClick={handleRemove}>
            <i className="fa-regular fa-circle-xmark"></i>
          </button>
        </Reveal>  
      </div>
      <div className={styles.cardContent}>
        <Tooltip content={assetItem.name || 'Untitled'} placement="bottom">
          <div className={styles.name}>
            {assetItem.name || 'Untitled'}
          </div>
        </Tooltip>
        <div className={styles.meta}>{fileSize && `${fileSize}`}</div>
      </div>
      </NavigateEntity>
    </div>
  );
}

interface PlaceholderAssetCardProps {
  height: number;
}

const PlaceholderAssetCard: React.FC<PlaceholderAssetCardProps> = ({ height = 280 }) => {
  return (
    <div className={cx(styles.card, styles.placeholder)}>
      <div className={cx(styles.icon, styles.block)} style={{height: height}}></div>
      <div className={styles.cardContent}>
        <div className={cx(styles.name, styles.block)}></div>
        <div className={cx(styles.meta, styles.block)}></div>
      </div>
    </div>
  );
}

export { AssetCard, type AssetCardProps, PlaceholderAssetCard };